// Copyright (C) 2023 Cochise Ruhulessin
//
// All rights reserved. No warranty, explicit or implicit, provided. In
// no event shall the author(s) be liable for any claim or damages.
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
import {
  IonButton,
  IonCol,
  IonGrid,
  IonItem,
  IonItemDivider,
  IonItemGroup,
  IonLabel,
  IonList,
  IonNote,
  IonRow,
  IonText
} from "@ionic/react"
import { useLocales } from "react-localized"
import ReactMarkdown from 'react-markdown'
import PinField from "react-pin-field"
import './AccessCodeInput.scss'
import { useEffect, useRef, useState } from "react"
import { ComponentBackdrop } from "../lib"


interface AccessCodeInputProps {
  continueLabel?: string
  onSubmit(code: string): Promise<boolean>
}


const AccessCodeInput: React.FC<AccessCodeInputProps> = ({
  continueLabel,
  onSubmit
}) => {
  const {gettext} = useLocales()
  const componentRef = useRef<HTMLIonColElement>(null)
  const ref = useRef<HTMLInputElement[]>(null)
  const [canContinue, setCanContinue] = useState<boolean>(false)
  const [failed, setFailed] = useState<boolean>(false)
  const [loading, setLoading] = useState<boolean>(false)
  const [value, setValue] = useState<string>("")
  const l = 6
  const clear: () => void = () => {
    if (!!ref.current) {
      ref.current.forEach((element, i) => {
        element.value = ""
      })
    }
  }

  useEffect(() => {
    if (!!failed) {
      setValue("")
    }
  }, [failed])

  useEffect(() => {
    if (failed) { clear() }
  }, [failed])

  useEffect(() => {
    setCanContinue(value.length === l)
    if (value.length === l && ref.current) {
      ref.current[l - 1].blur()
    }
  }, [value])
  return (
    <IonGrid style={{marginTop: "var(--section-padding)"}}>
      <IonRow className="panel">
        <IonCol ref={componentRef} size="12" sizeXs="12" sizeSm="8" sizeMd="6" sizeLg="4" sizeXl="3">
          <ComponentBackdrop parent={componentRef.current} visible={loading}/>
          <IonList lines="none">
            <IonItemGroup>
              <IonItemDivider>
                <IonLabel>{gettext("Authentication required")}</IonLabel>
              </IonItemDivider>
              <IonItem style={{marginBottom: "22px"}}>
                <IonText className="bold-primary">
                  <p>{gettext("Enter the access code you received via email or WhatsApp to view this document.")} </p>
                  <ReactMarkdown>
                    {gettext`Click or tap **${continueLabel}** to continue.`}
                  </ReactMarkdown>
                </IonText>
              </IonItem>
              <IonItem className="pin-field">
                <IonText>
                  <PinField
                    className="pin-field"
                    //format={(v: string) => { return '🙈'}}
                    length={l}
                    onChange={(code: string) => {
                      setFailed(false)
                      setValue(code)
                    }}
                    ref={ref}
                    type="numeric"
                  />
                </IonText>
                {(!!failed && value === "")
                  ? (<IonNote slot="error">{gettext("That access code was not valid.")}</IonNote>)
                  : (<IonNote slot="helper">{gettext`Enter the the access code.`}</IonNote>)
                }
              </IonItem>
            </IonItemGroup>
          </IonList>
          <IonButton
            className="text-start"
            fill="clear"
            onClick={() => {
              clear()
              setLoading(true)
              onSubmit(value)
                .then((success) => setFailed(!success))
                .finally(() => setLoading(false))
            }}
            disabled={!canContinue}
          >
            {continueLabel || gettext("Continue")}
          </IonButton>
        </IonCol>
      </IonRow>
    </IonGrid>
  )
}


export { AccessCodeInput }